import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  alpha,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTheme } from '@paneon/theme';
import type { Pet } from '../services/api';

interface AddPetDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: PetFormData) => Promise<void>;
  editPet?: Pet | null;
}

export interface PetFormData {
  name: string;
  species: string;
  breed?: string;
  birth_date?: string;
  weight_kg?: number;
  notes?: string;
}

const speciesOptions = [
  { value: 'dog', label: 'Hund', emoji: '🐕' },
  { value: 'cat', label: 'Katze', emoji: '🐱' },
  { value: 'bird', label: 'Vogel', emoji: '🐦' },
  { value: 'fish', label: 'Fisch', emoji: '🐟' },
  { value: 'rabbit', label: 'Kaninchen', emoji: '🐰' },
  { value: 'hamster', label: 'Hamster', emoji: '🐹' },
  { value: 'other', label: 'Sonstiges', emoji: '🐾' },
];

export function AddPetDialog({ open, onClose, onSave, editPet }: AddPetDialogProps) {
  const { isDarkMode } = useTheme();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState<PetFormData>({
    name: '',
    species: 'dog',
    breed: '',
    birth_date: '',
    weight_kg: undefined,
    notes: '',
  });

  // Reset form when dialog opens or editPet changes
  useEffect(() => {
    if (open) {
      if (editPet) {
        setForm({
          name: editPet.name,
          species: editPet.species,
          breed: editPet.breed || '',
          birth_date: editPet.birth_date || '',
          weight_kg: editPet.weight_kg || undefined,
          notes: editPet.notes || '',
        });
      } else {
        setForm({
          name: '',
          species: 'dog',
          breed: '',
          birth_date: '',
          weight_kg: undefined,
          notes: '',
        });
      }
    }
  }, [open, editPet]);

  const handleSubmit = async () => {
    if (!form.name.trim() || !form.species) return;

    setLoading(true);
    try {
      await onSave({
        ...form,
        name: form.name.trim(),
        breed: form.breed?.trim() || undefined,
        notes: form.notes?.trim() || undefined,
      });
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const dialogStyles = {
    '& .MuiDialog-paper': {
      borderRadius: 3,
      backgroundColor: isDarkMode ? alpha('#1a1a1a', 0.95) : alpha('#fff', 0.95),
      backdropFilter: 'blur(20px)',
      maxWidth: 400,
      width: '100%',
    },
  };

  const inputStyles = {
    '& .MuiOutlinedInput-root': {
      borderRadius: 2,
      backgroundColor: isDarkMode ? alpha('#fff', 0.05) : alpha('#000', 0.03),
    },
  };

  return (
    <Dialog open={open} onClose={onClose} sx={dialogStyles}>
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 1,
        }}
      >
        {editPet ? 'Haustier bearbeiten' : 'Neues Haustier'}
        <IconButton size="small" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField
            label="Name"
            value={form.name}
            onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
            required
            fullWidth
            sx={inputStyles}
            autoFocus
          />

          <FormControl fullWidth sx={inputStyles}>
            <InputLabel>Tierart</InputLabel>
            <Select
              value={form.species}
              label="Tierart"
              onChange={e => setForm(f => ({ ...f, species: e.target.value }))}
            >
              {speciesOptions.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.emoji} {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Rasse (optional)"
            value={form.breed}
            onChange={e => setForm(f => ({ ...f, breed: e.target.value }))}
            fullWidth
            sx={inputStyles}
          />

          <TextField
            label="Geburtsdatum"
            type="date"
            value={form.birth_date}
            onChange={e => setForm(f => ({ ...f, birth_date: e.target.value }))}
            fullWidth
            slotProps={{
              inputLabel: { shrink: true },
            }}
            sx={inputStyles}
          />

          <TextField
            label="Gewicht (kg)"
            type="number"
            value={form.weight_kg || ''}
            onChange={e =>
              setForm(f => ({
                ...f,
                weight_kg: e.target.value ? parseFloat(e.target.value) : undefined,
              }))
            }
            fullWidth
            slotProps={{
              htmlInput: { step: 0.1, min: 0 },
            }}
            sx={inputStyles}
          />

          <TextField
            label="Notizen (optional)"
            value={form.notes}
            onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
            fullWidth
            multiline
            rows={2}
            sx={inputStyles}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>
          Abbrechen
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading || !form.name.trim()}
          sx={{ textTransform: 'none', borderRadius: 2 }}
        >
          {loading ? 'Speichern...' : editPet ? 'Speichern' : 'Hinzufügen'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
